import type {
  ExpressionSpecification,
  GeoJSONSourceSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';

import type { LatLonPoint } from '@/types/barriers';

import { WAYPOINT_ICON_IMAGE_ID } from './waypointIcon';

export const DETOUR_WAYPOINTS_SOURCE_ID = 'detour-waypoints';
export const DETOUR_WAYPOINTS_LAYER_ID = 'detour-waypoints-dots';
export const DETOUR_WAYPOINTS_HOVERED_LAYER_ID = 'detour-waypoints-hovered';

/**
 * Data carried by one detour-waypoint feature.
 *
 * Each feature represents a single `auto_waypoints` entry belonging to
 * one applied barrier of one visible barrier-aware route. `route_id`
 * links it back to the originating route; `barrier_id` is the barrier
 * whose `auto_waypoints` list this dot comes from.
 */
export interface DetourWaypointProperties {
  readonly barrier_id: number;
  readonly route_id: string;
  readonly index: number;
}

const DETOUR_ICON_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 0.55,
  10, 0.8,
  14, 1.0,
];

const DETOUR_HOVERED_ICON_SIZE: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 0.75,
  10, 1.0,
  14, 1.25,
];

export function buildEmptyDetourCollection(): FeatureCollection<
  Point,
  DetourWaypointProperties
> {
  return { type: 'FeatureCollection', features: [] };
}

export function buildDetourWaypointsSource(): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data: buildEmptyDetourCollection(),
  };
}

export function buildDetourWaypointsLayer(): SymbolLayerSpecification {
  return {
    id: DETOUR_WAYPOINTS_LAYER_ID,
    type: 'symbol',
    source: DETOUR_WAYPOINTS_SOURCE_ID,
    layout: {
      'icon-image': WAYPOINT_ICON_IMAGE_ID,
      'icon-size': DETOUR_ICON_SIZE,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-opacity': 0.95,
    },
  };
}

/**
 * Larger waypoint icon drawn on top of the base detour layer when the
 * cursor hovers it. Filter is updated by `MainMap` to the hovered
 * `barrier_id` (the only stable property a detour-waypoint feature
 * carries on its own).
 */
export function buildDetourWaypointsHoveredLayer(): SymbolLayerSpecification {
  return {
    id: DETOUR_WAYPOINTS_HOVERED_LAYER_ID,
    type: 'symbol',
    source: DETOUR_WAYPOINTS_SOURCE_ID,
    filter: ['==', ['get', 'barrier_id'], -1],
    layout: {
      'icon-image': WAYPOINT_ICON_IMAGE_ID,
      'icon-size': DETOUR_HOVERED_ICON_SIZE,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: {
      'icon-opacity': 1,
    },
  };
}

export interface DetourWaypointInput {
  readonly routeId: string;
  readonly barrierId: number;
  readonly waypoints: readonly LatLonPoint[];
}

/**
 * Builds a FeatureCollection from per-route detour-waypoint inputs.
 *
 * Duplicates (same `(barrier_id, lat, lon)` coming from different
 * routes) are preserved intentionally — we attach the originating
 * `route_id` to each feature so hit detection can tell which route's
 * detour the user clicked.
 */
export function buildDetourWaypointsCollection(
  entries: readonly DetourWaypointInput[],
): FeatureCollection<Point, DetourWaypointProperties> {
  const features: Feature<Point, DetourWaypointProperties>[] = [];
  for (const entry of entries) {
    entry.waypoints.forEach((wp, index) => {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [wp.lon, wp.lat] },
        properties: {
          barrier_id: entry.barrierId,
          route_id: entry.routeId,
          index,
        },
      });
    });
  }
  return { type: 'FeatureCollection', features };
}
